// Considered: ArcStageListMolecule — rejected because it renders linear list rows (index, name,
// chip) for the browse drawer; a column head carries an editable name, three chip kinds, a
// requirements callout, two actions and selected/unreachable states — forcing the list row would
// break it everywhere else. See spec Reuse Audit (arc-editor-canvas-v1.md).
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import './ArcStageCardMolecule.scss';

export interface ArcStageCardTransition {
  kind: string;
  /** Iconify `lucide:*` name for the transition kind (`AC_TRANS_KINDS[kind].glyph`). */
  glyph: string;
  label: string;
  note?: string;
}

export interface ArcStageCardKeyDate {
  /** Already-localized chip text (`when.text`). */
  text: string;
  /** No answering date declared, or the anchor names a date the arc does not declare. */
  vacant?: boolean;
  /** The engine reads whole days only — this offset would be read as 0d. */
  offsetOk?: boolean;
}

export interface ArcStageCardStage {
  id: string;
  name: string;
  isTerminal?: boolean;
  note?: string;
  /** Already-localized requirement labels (`requires.map(r => acRefName(r.ref))`). */
  requires?: string[];
}

export interface ArcStageCardMoleculeProps {
  stage: ArcStageCardStage;
  /** 0-based position in the stage sequence. */
  index: number;
  /** The transition leading into this stage; absent for index 0 and for an unreachable stage. */
  transition?: ArcStageCardTransition;
  keyDate: ArcStageCardKeyDate;
  sla?: string;
  automated?: boolean;
  /** Count of manual exits besides the default (`offSpine.length`). */
  otherPathCount?: number;
  /** Count of storyboard default rules targeting this stage (`ruleCount`). */
  defaultRuleCount?: number;
  /** In-place rename control vs. static text (`canEdit`). */
  editable?: boolean;
  selected?: boolean;
  /** Nothing leads into this stage. */
  unreachable?: boolean;
  onSelect: () => void;
  onRename: (name: string) => void;
  onSelectTransition: () => void;
  onGoFlow: () => void;
  onGoStoryboard: () => void;
  className?: string;
}

/** A stage column head: ordinal, editable name, key-date/final/automated chips, a requirements
 * callout, and the two actions into Flow / Storyboard (`arc-lane.jsx:62-148`). */
const ArcStageCardMolecule: React.FC<ArcStageCardMoleculeProps> = ({
  stage,
  index,
  transition,
  keyDate,
  sla,
  automated = false,
  otherPathCount = 0,
  defaultRuleCount = 0,
  editable = false,
  selected = false,
  unreachable = false,
  onSelect,
  onRename,
  onSelectTransition,
  onGoFlow,
  onGoStoryboard,
  className = '',
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(stage.name);

  useEffect(() => {
    setName(stage.name);
  }, [stage.id, stage.name]);

  const requires = stage.requires ?? [];
  const keyDateWarn = Boolean(keyDate.vacant) || keyDate.offsetOk === false;

  return (
    <div
      className={`arc-stage-card ${className}`.trim()}
      data-selected={selected ? 'true' : undefined}
      data-unreachable={unreachable ? 'true' : undefined}
      data-datum="canvas.card#3"
      data-kind="card"
      data-testid={`arc-stage-card-${stage.id}`}
      // A pointer shortcut only: the card is not a focus stop. Its keyboard path is the grid's
      // column-head cell (`ArcLaneGridMolecule` roving tabindex — Enter there is `onStageActivate`),
      // because a card that holds an input and three buttons cannot itself be a button.
      onClick={onSelect}
    >
      {index > 0 && (
        <span className="arc-stage-card__transition" data-kind-value={transition?.kind ?? 'none'}>
          <ButtonAtom
            variant="text"
            size="small"
            shape="icon-square"
            icon={<GlyphAtom glyph={`lucide:${transition ? transition.glyph : 'unlink'}`} aria-hidden />}
            aria-label={
              transition
                ? t('studio.arcEditor.stageCard.transitionInto', { name: stage.name, label: transition.label })
                : t('studio.arcEditor.stageCard.noTransitionInto', { name: stage.name })
            }
            tooltip={transition ? transition.note : t('studio.arcEditor.stageCard.nothingLeadsHere')}
            onClick={(event) => {
              event.stopPropagation();
              if (transition) {
                onSelectTransition();
              } else {
                onGoFlow();
              }
            }}
            data-datum="canvas.button#2"
            data-kind="button"
          />
        </span>
      )}

      <div className="arc-stage-card__top">
        <span className="arc-stage-card__n" data-datum="canvas.count#2" data-kind="count">
          {index + 1}
        </span>
        {editable ? (
          <InputAtom
            className="arc-stage-card__name-input"
            value={name}
            aria-label={t('studio.arcEditor.stageCard.nameAriaLabel', { index: index + 1 })}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => onRename(name.trim() || stage.name)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                (event.target as HTMLInputElement).blur();
              }
            }}
            data-datum="canvas.input"
            data-kind="input"
          />
        ) : (
          <b className="arc-stage-card__name" data-datum="canvas.name" data-kind="value">
            {name}
          </b>
        )}
        {stage.isTerminal && (
          <TagAtom
            label={t('studio.arcEditor.stageCard.final')}
            tone="info"
            icon="lucide:flag"
            size="small"
            title={t('studio.arcEditor.stageCard.finalTitle')}
            data-datum="canvas.final"
            data-kind="chip"
          />
        )}
      </div>

      <div className="arc-stage-card__meta">
        <TagAtom
          label={keyDate.text}
          tone={keyDateWarn ? 'warn' : undefined}
          icon={keyDateWarn ? 'lucide:help-circle' : 'lucide:clock'}
          size="small"
          title={
            keyDate.offsetOk === false ? t('studio.arcEditor.stageCard.wholeDaysOnly') : undefined
          }
          data-datum="canvas.chip#2"
          data-kind="chip"
        />
        {sla && (
          <TagAtom
            label={t('studio.arcEditor.stageCard.target', { sla })}
            size="small"
            data-datum="canvas.chip#3"
            data-kind="chip"
          />
        )}
        {automated && (
          <TagAtom
            label={t('studio.arcEditor.stageCard.automated')}
            icon="lucide:bot"
            size="small"
            data-datum="canvas.automated"
            data-kind="chip"
          />
        )}
      </div>

      {stage.note && (
        <p className="arc-stage-card__note" data-datum="canvas.stage-note" data-kind="text">
          {stage.note}
        </p>
      )}

      {requires.length > 0 && (
        <div className="arc-stage-card__requires" data-datum="canvas.needs" data-kind="callout">
          <GlyphAtom glyph="lucide:list-checks" aria-hidden />
          <span>{t('studio.arcEditor.stageCard.needsBefore', { requires: requires.join(` ${t('studio.arcEditor.stageCard.and')} `) })}</span>
        </div>
      )}

      <div className="arc-stage-card__foot">
        {otherPathCount > 0 && (
          <ButtonAtom
            variant="secondary"
            size="small"
            icon={<GlyphAtom glyph="lucide:git-fork" aria-hidden />}
            label={t('studio.arcEditor.stageCard.otherPaths', { count: otherPathCount })}
            onClick={(event) => {
              event.stopPropagation();
              onGoFlow();
            }}
            data-datum="canvas.button#3"
            data-kind="button"
          />
        )}
        <ButtonAtom
          variant="secondary"
          size="small"
          icon={<GlyphAtom glyph="lucide:layout-grid" aria-hidden />}
          label={
            defaultRuleCount > 0
              ? t('studio.arcEditor.stageCard.defaultRules', { count: defaultRuleCount })
              : t('studio.arcEditor.stageCard.noDefaultRules')
          }
          onClick={(event) => {
            event.stopPropagation();
            onGoStoryboard();
          }}
          data-datum="canvas.button#4"
          data-kind="button"
        />
      </div>

      {unreachable && (
        <span
          className="arc-stage-card__unreachable"
          data-datum="canvas.unreachable-nothing-leads-here"
          data-kind="status-mark"
        >
          <GlyphAtom glyph="lucide:alert-triangle" aria-hidden />
          {t('studio.arcEditor.stageCard.unreachable')}
        </span>
      )}
    </div>
  );
};

ArcStageCardMolecule.displayName = 'ArcStageCardMolecule';

export default ArcStageCardMolecule;
